import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Enums (mirror C# Common.Enums) ────────────────────────────────────────

export type StopOrderPolicy = "Strict" | "AllowOutOfOrder";
export type RoutePointType = "MainStop" | "CollectionPoint";

// ── DTOs ──────────────────────────────────────────────────────────────────

export interface RouteStopDto {
  id: string;
  routeTemplateId: string;
  transportRateId?: string;
  stopName: string;
  pointType: RoutePointType;
  sequence: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  town?: string;
  region?: string;
  deliveryPrice?: number;
  transportRateCost?: number;
  estimatedArrivalOffsetMinutes?: number;
  isCollectionPoint: boolean;
  isActive: boolean;
  notes?: string;
  isMapped: boolean;
  destinationLatitude?: number;
  destinationLongitude?: number;
}

export interface RouteTemplateDto {
  id: string;
  name: string;
  code: string;
  departureName: string;
  departureLatitude?: number;
  departureLongitude?: number;
  finalDestinationName?: string;
  finalDestinationTransportRateId?: string;
  majorTown?: string;
  region?: string;
  estimatedDistanceKm?: number;
  estimatedDurationMinutes?: number;
  isActive: boolean;
  stopOrderPolicy: StopOrderPolicy;
  notes?: string;
  stops: RouteStopDto[];
  createdAt: string;
  updatedAt?: string;
}

export interface RouteTemplateSummaryDto {
  id: string;
  name: string;
  code: string;
  departureName: string;
  finalDestinationName?: string;
  majorTown?: string;
  region?: string;
  estimatedDistanceKm?: number;
  estimatedDurationMinutes?: number;
  isActive: boolean;
  stopOrderPolicy: StopOrderPolicy;
  stopCount: number;
  stops: RouteStopDto[];
}

export interface RouteTemplateListResultDto {
  items: RouteTemplateSummaryDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateRouteStopRequest {
  transportRateId?: string;
  stopName: string;
  pointType: RoutePointType;
  sequence: number;
  address?: string;
  town?: string;
  estimatedArrivalOffsetMinutes?: number;
  isCollectionPoint: boolean;
  notes?: string;
}

export interface CreateRouteTemplateRequest {
  name: string;
  code?: string;
  departureName: string;
  departureLatitude?: number;
  departureLongitude?: number;
  finalDestinationName?: string;
  finalDestinationTransportRateId?: string;
  majorTown?: string;
  region?: string;
  estimatedDistanceKm?: number;
  estimatedDurationMinutes?: number;
  stopOrderPolicy: StopOrderPolicy;
  notes?: string;
  stops: CreateRouteStopRequest[];
}

export interface UpdateRouteStopRequest {
  id?: string;
  transportRateId?: string;
  stopName: string;
  pointType: RoutePointType;
  sequence: number;
  address?: string;
  town?: string;
  estimatedArrivalOffsetMinutes?: number;
  isCollectionPoint: boolean;
  isActive: boolean;
  notes?: string;
}

export interface UpdateRouteTemplateRequest {
  id?: string;
  name: string;
  code?: string;
  departureName: string;
  departureLatitude?: number;
  departureLongitude?: number;
  finalDestinationName?: string;
  finalDestinationTransportRateId?: string;
  majorTown?: string;
  region?: string;
  estimatedDistanceKm?: number;
  estimatedDurationMinutes?: number;
  isActive: boolean;
  stopOrderPolicy: StopOrderPolicy;
  notes?: string;
  stops: UpdateRouteStopRequest[];
}

export interface RouteTemplateListQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
}

// ── API ────────────────────────────────────────────────────────────────────

export const routeTemplatesApi = {
  getAll: async (): Promise<RouteTemplateDto[]> => {
    const res = await apiClient.get<ApiResponse<RouteTemplateDto[]>>("/route-templates");
    return res.data.data ?? [];
  },

  getActive: async (): Promise<RouteTemplateSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<RouteTemplateSummaryDto[]>>("/route-templates/active");
    return res.data.data ?? [];
  },

  getPaged: async (query: RouteTemplateListQuery): Promise<RouteTemplateListResultDto> => {
    const res = await apiClient.get<ApiResponse<RouteTemplateListResultDto>>("/route-templates/paged", {
      params: query,
    });
    return res.data.data;
  },

  getById: async (id: string): Promise<RouteTemplateDto> => {
    const res = await apiClient.get<ApiResponse<RouteTemplateDto>>(`/route-templates/${id}`);
    return res.data.data;
  },

  create: async (data: CreateRouteTemplateRequest): Promise<RouteTemplateDto> => {
    const res = await apiClient.post<ApiResponse<RouteTemplateDto>>("/route-templates", data);
    return res.data.data;
  },

  update: async (id: string, data: UpdateRouteTemplateRequest): Promise<RouteTemplateDto> => {
    const res = await apiClient.put<ApiResponse<RouteTemplateDto>>(`/route-templates/${id}`, {
      ...data,
      id,
    });
    return res.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/route-templates/${id}`);
  },
};
